import { HelmrMark, Icon, useMediaQuery } from "@helmr/ui";
import { useEffect, useRef, useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { MobileShell } from "./mobile/MobileShell";
import { PHONE_QUERY } from "./mobile/query";
import { AccountMenu } from "./AccountMenu";
import { BellMenu } from "./BellMenu";
import { SearchPalette } from "./SearchPalette";
import { Sidebar } from "./Sidebar";
import { Switcher } from "./Switcher";
import { listBusinesses } from "../api/auth";
import { useSessionSnapshot } from "./permissions";
import { SupportBanner } from "./SupportBanner";
import { useServices } from "./services";
import { strings } from "./strings";
import { useQuery } from "@tanstack/react-query";
import s from "./shell.module.css";

const t = strings.shell;

/** Below 768px a phone gets the native-feeling shell (D56); from 768px up, the sidebar shell. One media query, no user-agent sniffing. */
export function AppLayout() {
  const phone = useMediaQuery(PHONE_QUERY);
  // Same route element, same router, same QueryClient above: resizing across 768px keeps the route and the cache.
  const shell = phone ? <MobileShell /> : <DesktopShell />;
  // A Helmr support session (console-support) gets a banner above both shells; it sets --support-bar for them (SupportBanner).
  return <><SupportBanner />{shell}</>;
}

/** 56px dark top bar, 240px sidebar, content capped at 1060px. */
function DesktopShell() {
  const { client, session } = useServices();
  const state = useSessionSnapshot();
  const location = useLocation();
  const [navOpen, setNavOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const hambRef = useRef<HTMLButtonElement>(null);

  // The picker is kept after sign-in; after a reload it is fetched once so the switcher has its branches.
  const picker = useQuery({
    queryKey: ["auth", "businesses", state.businessId],
    queryFn: () => listBusinesses(client),
    enabled: state.picker.length === 0,
    staleTime: 5 * 60_000,
  });
  useEffect(() => { if (picker.data && state.picker.length === 0) session.setPicker(picker.data); }, [picker.data, state.picker.length, session]);

  useEffect(() => { setNavOpen(false); }, [location.pathname]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setPaletteOpen((o) => !o); }
      else if (e.key === "Escape" && navOpen) { setNavOpen(false); hambRef.current?.focus(); }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [navOpen]);
  useEffect(() => { if (navOpen) document.querySelector<HTMLElement>("#side a")?.focus(); }, [navOpen]);

  return (
    <div className={s.app}>
      <a className={s.skip} href="#content">{t.skip}</a>
      <header className={s.top}>
        <div className={s.brand}>
          <button ref={hambRef} className={`${s.ibtn} ${s.hamb}`} aria-label={t.openNav} aria-controls="side" aria-expanded={navOpen} onClick={() => setNavOpen((o) => !o)}>
            <Icon name="menu" />
          </button>
          <Link to="/home" aria-label="Helmr home"><HelmrMark tone="dark" /></Link>
          <Switcher variant="top" />
        </div>
        <button className={s.search} aria-label={t.searchLong} onClick={() => setPaletteOpen(true)}>
          <Icon name="search" />{t.searchPlaceholder}<span className={s.kbd}>⌘K</span>
        </button>
        <div className={s.tr}>
          <button className={`${s.ibtn} ${s.sbtn}`} aria-label={t.search} onClick={() => setPaletteOpen(true)}><Icon name="search" /></button>
          <BellMenu />
          <AccountMenu />
        </div>
      </header>
      <div className={s.body}>
        <Sidebar open={navOpen} onNavigate={() => setNavOpen(false)} />
        {navOpen ? <div className={s.navScrim} onClick={() => setNavOpen(false)} data-testid="nav-scrim" /> : null}
        <main className={s.main} id="content">
          <div className={s.wrap}><Outlet /></div>
        </main>
      </div>
      <SearchPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
    </div>
  );
}
